/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtom, useStateXAtomValue } from '@cloudio-saas/statex';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AnyItem,
  AnyManager,
  BooleanExpressionValue,
  ExpressionValue,
  getAllManagers,
  getManager,
  ItemType,
  Page,
  Platform,
  PropertyDefinition,
} from 'componentregistry';
import Icon from 'icons/Icon';
import { sidebarActiveComponentAtom } from 'layout/Sidebar/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useLayoutEffect, useState } from 'react';
import { EMPTY_PAGE } from 'root/Resource';
import { useUpdateOnHMR } from 'utils/hmr';
import Component from './Component';
import ComponentSampleRenderer, {
  maximizedSampleIdAtom,
} from './ComponentSampleRenderer';
import Markdown from './Markdown';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: 16,
    backgroundColor: theme.palette.background.paper,
    userSelect: 'text',
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.text.primary,
  },
  heading: {
    paddingTop: 26,
    fontSize: theme.typography.pxToRem(26),
    fontWeight: theme.typography.fontWeightRegular,
  },
  wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    margin: -4,
    paddingTop: 8,
  },
}));

export function getAllowedParentComponents({
  page,
  appUid,
  pageId,
  platform,
  itemType,
}: {
  page: Page;
  appUid: string;
  pageId: string;
  platform: Platform;
  itemType: ItemType;
}): {
  isChildAllowed: string | boolean;
  isAllowedInsideParent?: string | boolean | undefined;
  m: AnyManager;
}[] {
  const mgr = getManager(itemType);
  return getAllManagers()
    .map(
      (
        m,
      ): {
        isChildAllowed: string | boolean;
        isAllowedInsideParent?: string | boolean;
        m: AnyManager;
      } => {
        const parentItem = makeEmptyItem(m);

        const isChildAllowed = m.isChildAllowed(page, parentItem, itemType, {
          appUid,
          pageId,
          itemId: 'x',
          mgr,
          platform,
        });
        if (typeof isChildAllowed === 'string' || !isChildAllowed) {
          return { isChildAllowed, m };
        }
        const isAllowedInsideParent = mgr.isAllowedInsideParent(
          page,
          parentItem,
          {
            appUid,
            pageId,
            itemId: parentItem.itemId,
            mgr,
            platform,
          },
        );
        if (
          !isAllowedInsideParent ||
          typeof isAllowedInsideParent === 'string'
        ) {
          return { isChildAllowed: false, isAllowedInsideParent: false, m };
        }
        return {
          isChildAllowed,
          isAllowedInsideParent: isChildAllowed,
          m,
        };
      },
    )
    .filter(
      (row) => row.isChildAllowed !== false && !row.m.hideInComponentsList,
    );
}

function makeEmptyItem(mgr: AnyManager) {
  const item: AnyItem = {
    children: [],
    props: { ...mgr.defaultValue },
    itemId: 'x',
    parent: '',
    type: mgr.type,
  };
  return item;
}

export default function ComponentHelp({
  appUid,
  roleUid,
  devMode,
  pageId,
  width,
}: {
  appUid: string;
  roleUid: string;
  devMode: boolean;
  pageId: string;
  width: number;
}): React.ReactElement {
  const classes = useStyles();
  const [itemType, setItemType] = useStateXAtom(sidebarActiveComponentAtom);
  const mgr = getManager(itemType);
  const item = makeEmptyItem(mgr);
  const page = EMPTY_PAGE;
  const [items, setItems] = useState<AnyManager[]>([]);
  useUpdateOnHMR();
  const platform = usePlatform();

  useLayoutEffect(() => {
    setItems((old) => {
      const index = old.findIndex((m) => mgr.type === m.type);
      if (index !== -1) {
        return old.slice(0, index + 1);
      }
      return [...old, mgr];
    });
  }, [mgr]);

  const allowedChildren = getAllManagers()
    .map(
      (
        m,
      ): {
        isChildAllowed: string | boolean;
        isAllowedInsideParent?: string | boolean;
        m: AnyManager;
      } => {
        const isChildAllowed = mgr.isChildAllowed(page, item, m.type, {
          appUid,
          pageId,
          itemId: item.itemId,
          mgr,
          platform,
        });
        if (typeof isChildAllowed === 'string' || !isChildAllowed) {
          return { isChildAllowed, m };
        }
        // const isAllowedInsideParent = m.isAllowedInsideParent(page, item);
        // if (typeof isAllowedInsideParent === 'string') {
        //   return { isChildAllowed, isAllowedInsideParent, m };
        // }
        return {
          isChildAllowed,
          isAllowedInsideParent: isChildAllowed,
          m,
        };
      },
    )
    .filter((row) => row.isChildAllowed !== false);

  const allowedParentComponents = getAllowedParentComponents({
    page,
    appUid,
    pageId,
    platform,
    itemType: item.type,
  });

  let icon = mgr.icon;
  if (typeof icon === 'function') {
    icon = icon({
      type: mgr.type,
      children: [],
      parent: item.itemId,
      itemId: 'x',
      props: mgr.defaultValue,
    });
  }

  const allowedComponents = allowedChildren.filter(
    (m) => m.m.managerType === 'Component',
  );
  const allowedTriggers = allowedChildren.filter(
    (m) => m.m.managerType === 'Trigger',
  );
  const allowedActions = allowedChildren.filter(
    (m) => m.m.managerType === 'Action',
  );
  const maximizedPageId = useStateXAtomValue(maximizedSampleIdAtom);
  let { helpRenders } = mgr;
  const maximized = maximizedPageId.startsWith(`${mgr.type}-`);
  if (maximized) {
    helpRenders = helpRenders.filter(
      (r) => maximizedPageId === `${mgr.type}-${r.exampleId}`,
    );
  }
  const propKeys = Object.keys(mgr.props);

  return (
    <div className={classes.root}>
      {!maximized ? (
        <Breadcrumbs maxItems={3}>
          {items.slice(0, items.length - 1).map((m) => {
            let iconVal = m.icon;
            if (typeof iconVal === 'function') {
              iconVal = iconVal({
                type: m.type,
                children: [],
                parent: item.itemId,
                itemId: 'x',
                props: m.defaultValue,
              });
            }
            return (
              <Button
                key={m.type}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{ display: 'flex', alignItems: 'center' }}
                color="inherit"
                onClick={() => {
                  setItemType(m.type);
                }}
              >
                <Icon sx={{ mr: 1 }} {...iconVal} />
                {m.name}
              </Button>
            );
          })}
          <Button
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
            color="primary"
          >
            <Icon sx={{ mr: 1 }} {...icon} />
            {mgr.name}
          </Button>
        </Breadcrumbs>
      ) : null}

      {mgr.deprecated ? (
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          <strong style={{ marginRight: 16 }}>Deprecated!</strong>
          {mgr.deprecated}
        </Alert>
      ) : null}

      {mgr.managerType === 'Component' && !maximized ? (
        mgr.layout === 'sizeAware' ? (
          <Alert>
            Layout: Size Aware. A Size Aware layout component can be rendered
            inside either Fixed or Auto layouts. If rendered inside a Fixed
            layout, it will make use of the size (width &amp; height) provided.
          </Alert>
        ) : mgr.layout === 'fixed' ? (
          <Alert>
            Layout: Fixed. A Fixed layout component requires width &amp; height
            in pixels to be passed from the parent component. Hence, it cannot
            be added if any of the parent components are of the Auto layout. A
            Fixed layout component is designed to split the available space in
            the required proportion based on the child components and pass the
            appropriate width and height to each of the child components.
          </Alert>
        ) : mgr.layout === 'auto' && mgr.category !== 'Data Grid' ? (
          <Alert>
            Layout: Auto. An Auto layout component doesn&apos;t require size
            information (width &amp; height) to render. Hence, it can be
            rendered under any layout.
          </Alert>
        ) : null
      ) : null}

      {mgr.managerType === 'Trigger' && !maximized ? (
        <Alert>
          Trigger: Triggers are non UI components that don&apos;t render
          anything visible on the screen. They are added to other visible
          components to react to user actions. e.g. Trigger On Click can be
          added to a Button to perform actions when the user presses the tutton.
        </Alert>
      ) : null}

      {mgr.managerType === 'Action' && !maximized ? (
        <Alert>
          Action: Actions are non UI components that don&apos;t render anything
          visible on the screen. They are attached to a Trigger or nested under
          other actions where applicable. Following are some examples for which
          actions can be used
          <ol>
            <li>Hide/Show any UI Component on the screen</li>
            <li>Update any properties of the UI Components</li>
            <li>Navigate to another page or external URL</li>
            <li>Invoke a Cloud Function</li>
            <li>Publish a business event</li>
            <li>Create a new record in a Data Store</li>
            <li>Update an attribute value of a Data Store record</li>
            <li>Delete an existing record from a Data Store</li>
            <li>Query/refresh the data via. Data Stores</li>
            <li>Post the user entered data to the server</li>
            <li>
              Loop through all or selected records in a Data Store and perform
              nested actions on each of the matched records
            </li>
            <li>Set the cursor to focus an input field</li>
            <li>Open/Close a Popup</li>
            <li>Reset user changes to a form/editable grid</li>
            <li>Display message to the user</li>
            <li>
              Get the user&apos;s confirmation before performing any
              irreversible action. e.g. Deleting a record.
            </li>
          </ol>
        </Alert>
      ) : null}

      {mgr.description && !maximized ? (
        <>
          <Typography className={classes.heading}>Description</Typography>
          <Divider />
          <Markdown>{mgr.description}</Markdown>
        </>
      ) : null}
      {!maximized ? (
        <>
          <Typography className={classes.heading}>Properties</Typography>
          <Divider />
          {propKeys.length ? (
            <ul>
              {propKeys.map((key) => {
                // @ts-ignore key
                const prop = mgr.props[key] as PropertyDefinition;
                const defaultValue = mgr.defaultValue[key];
                let strVal = '';
                switch (prop.type) {
                  case 'expression':
                    strVal = (defaultValue as ExpressionValue).value;
                    break;
                  case 'booleanExpression':
                    strVal = (defaultValue as BooleanExpressionValue).value
                      ? 'On'
                      : 'Off';
                    break;
                  case 'boolean':
                    strVal = defaultValue ? 'On' : 'Off';
                    break;
                  case 'radio':
                  case 'select': {
                    if (typeof prop.options === 'function') {
                      strVal = 'Dynamic Options';
                    } else {
                      const val = prop.options[defaultValue as string];
                      strVal =
                        typeof val === 'string' ? val : JSON.stringify(val);
                    }
                    break;
                  }
                  case 'padding':
                  case 'margin':
                  case 'border':
                  case 'iconselect':
                  case 'LookupCodeChooser':
                  case 'grid':
                    strVal = '';
                    break;
                  default:
                    switch (typeof defaultValue) {
                      case 'string':
                        strVal = defaultValue;
                        break;
                      case 'number':
                        if (defaultValue === Number.MIN_VALUE) {
                          strVal = '';
                        } else {
                          strVal = String(defaultValue);
                        }
                        break;
                      default:
                        strVal = JSON.stringify(defaultValue);
                    }
                }

                return prop.hidden ? null : (
                  <li key={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{prop.label}</Typography>
                      {typeof prop.helpText === 'string' ? (
                        <Icon
                          iconName="comment-exclamation"
                          data-tip={prop.helpText}
                          data-tip-delay={0}
                          data-tip-at="top"
                        />
                      ) : null}
                      {strVal ? (
                        <Typography variant="caption">({strVal})</Typography>
                      ) : null}
                    </Box>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Typography>None</Typography>
          )}
        </>
      ) : null}
      {helpRenders.map((h) => (
        <ComponentSampleRenderer
          key={`${mgr.type}-${h.exampleId}`}
          sample={h}
          appUid={appUid}
          roleUid={roleUid}
          width={width - 32}
          platform={platform}
        />
      ))}

      {mgr.managerType === 'Component' && !maximized ? (
        <>
          <Typography className={classes.heading}>
            Allowed Child Components ({allowedComponents.length})
          </Typography>
          <Divider />
          {allowedComponents.length === 0 ? (
            <Typography>None</Typography>
          ) : (
            <div className={classes.wrapper}>
              {allowedComponents.map((m) => {
                return (
                  <Component
                    appUid={appUid}
                    devMode={false}
                    key={m.m.type}
                    mgr={m.m}
                    pageId={pageId}
                    suffix="ac"
                  />
                );
              })}
            </div>
          )}
          <Typography className={classes.heading}>
            Allowed Triggers ({allowedTriggers.length})
          </Typography>
          <Divider />
          {allowedTriggers.length === 0 ? (
            <Typography>None</Typography>
          ) : (
            <div className={classes.wrapper}>
              {allowedTriggers.map((m) => {
                return (
                  <Component
                    appUid={appUid}
                    devMode={false}
                    key={m.m.type}
                    mgr={m.m}
                    pageId={pageId}
                    suffix="at"
                  />
                );
              })}
            </div>
          )}
        </>
      ) : null}

      {mgr.managerType !== 'Component' && !maximized ? (
        <>
          <Typography className={classes.heading}>
            Allowed Actions ({allowedActions.length})
          </Typography>
          <Divider />
          {allowedActions.length === 0 ? (
            <Typography>None</Typography>
          ) : (
            <div className={classes.wrapper}>
              {allowedActions
                .filter((m) => !m.m.hideInComponentsList)
                .map((m) => {
                  return (
                    <Component
                      appUid={appUid}
                      devMode={false}
                      key={m.m.type}
                      mgr={m.m}
                      pageId={pageId}
                      suffix="aa"
                    />
                  );
                })}
            </div>
          )}
        </>
      ) : null}
      {maximized ? null : (
        <>
          <Typography className={classes.heading}>
            Allowed Parent Components ({allowedParentComponents.length})
          </Typography>
          <Divider />
          <div className={classes.wrapper}>
            {allowedParentComponents.map((m) => {
              return (
                <Component
                  appUid={appUid}
                  devMode={false}
                  key={m.m.type}
                  mgr={m.m}
                  pageId={pageId}
                  suffix="apc"
                />
              );
            })}
          </div>
        </>
      )}
      {/* <Box
        sx={{
          position: 'relative',
          height: 600,
          marginTop: '20px',
          boxSizing: 'border-box',
        }}
      >
        <Chat
          appUid="cloudio"
          contextId="ComponentHelp"
          contextValue={itemType}
          header={`Discussions About '${mgr.name}'`}
          pageUid="ComponentHelp"
          placeholder="What are your thoughts?"
          platform={platform}
        />
      </Box> */}
    </div>
  );
}
